/**
 * UI helpers agent-specific, utilisables côté client ET serveur.
 * (Pas de directive "use client" — pure fonctions utilitaires.)
 */

/**
 * Couleur halo / glow par agent, pour les cards et arrière-plans des avatars.
 * Palette INNOVA6 + variantes cohérentes par métier.
 */
export function agentGlow(slug: string): string {
  switch (slug) {
    case "orchestrateur":
      return "rgba(232, 70, 31, 0.35)"; // orange
    case "strategiste":
      return "rgba(180, 100, 211, 0.3)"; // purple
    case "createur-contenu":
      return "rgba(245, 116, 171, 0.3)"; // pink
    case "designer":
      return "rgba(245, 116, 68, 0.3)"; // orange clair
    case "analyste":
      return "rgba(96, 165, 250, 0.3)"; // sky
    case "presentateur":
      return "rgba(232, 70, 31, 0.3)";
    case "gmail":
      return "rgba(245, 158, 11, 0.3)"; // amber
    case "fireflies":
      return "rgba(180, 100, 211, 0.3)";
    case "cv":
      return "rgba(16, 185, 129, 0.3)"; // emerald
    case "proposition":
      return "rgba(16, 185, 129, 0.32)"; // emerald (deals gagnés)
    case "comptabilite":
      return "rgba(96, 165, 250, 0.3)"; // blue (chiffres)
    case "mimo":
      return "rgba(16, 185, 129, 0.3)"; // emerald (assistance rassurante)
    case "jojo":
      return "rgba(245, 158, 11, 0.3)"; // amber (accueil chaleureux)
    default:
      return "rgba(232, 70, 31, 0.3)";
  }
}

/**
 * Mapping agent → fichier avatar dans /public/avatars/.
 * Cache-bust via query `?v=` : bump à chaque remplacement d'image pour forcer
 * les navigateurs à recharger (sinon Safari/Chrome gardent la version précédente).
 */
const AVATAR_VERSION = "5-cutout"; // refonte Althea — figurines Funko Pop détourées par rembg (juin 2026)
const v = `?v=${AVATAR_VERSION}`;

export const AVATAR_MAP: Record<string, string> = {
  orchestrateur: `/avatars/funko-bearded-headset.png${v}`,
  strategiste: `/avatars/funko-glasses-pen.png${v}`,
  "createur-contenu": `/avatars/funko-curly-book.png${v}`,
  designer: `/avatars/funko-glasses-laptop.png${v}`,
  analyste: `/avatars/funko-glasses-pen.png${v}`, // partagé avec stratège
  presentateur: `/avatars/funko-clean-wave.png${v}`,
  gmail: `/avatars/funko-curly-phone.png${v}`,
  fireflies: `/avatars/funko-bearded-headset.png${v}`, // partagé avec orchestrateur
  cv: `/avatars/funko-blonde-headset.png${v}`,
  ecommerce: `/avatars/funko-curly-phone.png${v}`, // partagé avec gmail
  prospection: `/avatars/funko-glasses-pen.png${v}`, // partagé avec stratège
  proposition: `/avatars/funko-bearded-headset.png${v}`, // closer confiant
  comptabilite: `/avatars/funko-blonde-headset.png${v}`, // rigueur / chiffres
  mimo: `/avatars/funko-blonde-headset.png${v}`, // casque = support client
  jojo: `/avatars/funko-curly-phone.png${v}`, // téléphone = accueil/standard
};

export const DEFAULT_AVATAR = `/avatars/funko-clean-wave.png${v}`;

/**
 * Agents migrés vers l'avatar "icône de métier" (pictogramme + rôle écrit
 * dessus) à la place de la figurine Funko -- voir AgentRoleIcon. Ajouter un
 * slug ici le bascule PARTOUT où AgentAvatar est utilisé (page agent, accueil,
 * dashboard, découvrir...), sans toucher chaque appelant. Icônes/rôles repris
 * de AGENT_UI / ROLES dans lib/agents.ts (dupliqués ici car ce fichier doit
 * rester utilisable côté client, sans les imports fs/gray-matter serveur).
 */
export const ICON_MODE_AGENTS: Record<string, { icon: string; role: string }> = {
  orchestrateur: { icon: "Network", role: "Chef d'orchestre" },
  strategiste: { icon: "Compass", role: "Stratège" },
  "createur-contenu": { icon: "PenLine", role: "Créateur de contenu" },
  designer: { icon: "Sparkles", role: "Creative Strategist" },
  analyste: { icon: "LineChart", role: "Analyste" },
  presentateur: { icon: "Presentation", role: "Présentateur" },
  gmail: { icon: "Mail", role: "Assistant email" },
  fireflies: { icon: "Mic", role: "Analyste de calls" },
  proposition: { icon: "FileSignature", role: "Proposition commerciale" },
  cv: { icon: "UserCheck", role: "Recruteuse" },
  ecommerce: { icon: "ShoppingBag", role: "Agente e-commerce" },
  prospection: { icon: "Radar", role: "Agent prospection" },
  veille: { icon: "TrendingUp", role: "Veille tendances" },
  comptabilite: { icon: "Calculator", role: "Comptable" },
  mimo: { icon: "LifeBuoy", role: "Service client" },
  jojo: { icon: "PhoneCall", role: "Standardiste" },
};
